import { ExclamationCircleFilled, FileExcelOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Divider, Form, Modal, Row, Upload, UploadProps, Col } from 'antd';
import { UTCNoShiftDatePicker } from 'src/components/v2/DatePicker';
import { useForm } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addLeavePackage } from 'src/apis/hr_ticket/leavePackage.api';
import { getEmployeeShortsDropdown } from 'src/apis/master_data/employee.api';
import { DepartmentTreeMultipleSelect } from 'src/components/v1/Select';
import EmployeeGroupMultipleSelectV2 from 'src/components/v1/Select/EmployeeGroupMultipleSelectV2';
import { ILeavePackageExclusion, LeavePackageAdd } from 'src/types/hr_ticket/leavePackage.type';
import { showNotification } from 'src/utils/notification';

const { confirm } = Modal;

type FormStateType = {
  departmentList: string[];
  employeeList: string[];
  date?: Date;
  note: string;
};

const initialFormState: FormStateType = {
  departmentList: [],
  employeeList: [],
  date: new Date(),
  note: ''
};

const uploadProps = {
  beforeUpload: () => {
    return false;
  },
  accept: '.xlsx,.xls'
};

interface AddLeavePackageModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
}

export default function AddLeavePackageModal({ visible, onCancel, onSuccess }: AddLeavePackageModalProps) {
  const { t } = useTranslation();
  const [form] = useForm();
  const [formState, setFormState] = useState<FormStateType>(initialFormState);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!visible) {
      setFormState(initialFormState);
      form.resetFields();
    }
  }, [visible, form]);

  const employeesDropdownQuery = useQuery({
    queryKey: ['employeeShortsDropdownV2'],
    queryFn: () => getEmployeeShortsDropdown(),
    staleTime: 60 * 1000
  });

  const addLeavePackageMutation = useMutation({
    mutationFn: (leavePackage: LeavePackageAdd) => addLeavePackage(leavePackage),
    onSuccess: () => {
      showNotification(t('notification.success.add'), 'success');
      setFormState(initialFormState);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['leavePackages'] });
      if (onSuccess) onSuccess();
      onCancel();
    }
  });

  const handleConfirmAdd = () => {
    confirm({
      title: 'Xác nhận tạo phiếu nghỉ phép hàng loạt?',
      icon: <ExclamationCircleFilled />,
      cancelText: 'Huỷ bỏ',
      okText: 'Đồng ý',
      centered: true,
      content:
        'Việc tạo phiếu nghỉ phép hàng loạt có thể gây ảnh hưởng đến toàn bộ nhân viên trong công ty. Bạn chắc chắn muốn tiếp tục.',
      onOk() {
        const leavePackageExclusions: Partial<ILeavePackageExclusion>[] = [];

        formState.departmentList.forEach((item) =>
          leavePackageExclusions.push({
            departmentId: JSON.parse(item)
          })
        );
        formState.employeeList.forEach((item) =>
          leavePackageExclusions.push({
            employeeId: Number(item)
          })
        );

        const leavePackage: LeavePackageAdd = {
          date: formState.date,
          note: formState.note,
          leavePackageExclusions: leavePackageExclusions
        };

        addLeavePackageMutation.mutate(leavePackage);
      }
    });
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then(handleConfirmAdd)
      .catch(() => {});
  };

  const handleChangeAttach: UploadProps['onChange'] = ({ fileList }) => {
    if (!fileList) return;

    const reader = new FileReader();
    reader.readAsArrayBuffer(fileList?.reverse()?.[0].originFileObj as Blob);
    reader.onload = async (e: ProgressEvent<FileReader>) => {
      const buffer = e.target?.result as ArrayBuffer;
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);
      const worksheet = workbook.getWorksheet();
      const employeeList: string[] = [];

      worksheet?.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          const employee = employeesDropdownQuery.data?.data.find((x) => x.code === row.getCell(2).value);
          if (employee) {
            employeeList.push(String(employee.id));
          }
        }
      });

      setFormState((prev) => ({ ...prev, employeeList }));
    };
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      title={<span style={{ fontWeight: 600, fontSize: 22 }}>{t('action.add') + ' Package nghỉ phép'}</span>}
      footer={null}
      destroyOnClose
      centered
      bodyStyle={{ padding: 40, background: '#fafbfc', borderRadius: 16 }}
      style={{ maxWidth: 800, minWidth: 600, margin: '32px auto' }}
    >
      {/* Thông tin bổ sung */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <div style={{ fontWeight: 500, color: '#888' }}>Người tạo:</div>
          <div style={{ fontWeight: 600 }}>Đỗ Đặng Quang Thịnh</div>
        </Col>
        <Col span={8}>
          <div style={{ fontWeight: 500, color: '#888' }}>Ngày tạo:</div>
          <div style={{ fontWeight: 600 }}>{dayjs().format('DD/MM/YYYY HH:mm')}</div>
        </Col>
        <Col span={8}>
          <div style={{ fontWeight: 500, color: '#888' }}>Loại package:</div>
          <div style={{ fontWeight: 600 }}>Nghỉ phép hàng loạt</div>
        </Col>
      </Row>
      <Divider style={{ margin: '0 0 24px 0' }} />
      <Form
        wrapperCol={{ span: 24 }}
        form={form}
        layout='vertical'
        fields={[
          {
            name: 'date',
            value: dayjs(formState.date)
          },
          {
            name: 'departmentList',
            value: formState.departmentList
          },
          {
            name: 'employeeList',
            value: formState.employeeList
          },
          {
            name: 'note',
            value: formState.note
          }
        ]}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('columnTitle.leavePackage.date')}
              name='date'
              rules={[{ required: true, message: t('required.leavePackage.date').toString() }]}
            >
              {' '}
              <UTCNoShiftDatePicker
                style={{ width: '100%' }}
                onChangeUTC={(valueUTC) => setFormState((prev) => ({ ...prev, date: valueUTC || undefined }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t(`columnTitle.leavePackage.department`)} name='departmentList'>
              <DepartmentTreeMultipleSelect
                style={{ width: '100%' }}
                onChange={(value: string[]) => setFormState((prev) => ({ ...prev, departmentList: value }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label={t(`columnTitle.leavePackage.employee`)} style={{ position: 'relative', marginBlockEnd: 0 }}>
          <Row gutter={8} align='middle'>
            <Col flex='auto'>
              <Form.Item name='employeeList' wrapperCol={{ span: 24 }} style={{ marginBlockEnd: 0 }}>
                <EmployeeGroupMultipleSelectV2
                  style={{ width: '100%' }}
                  maxTagCount='responsive'
                  onChange={(value: string[]) => setFormState((prev) => ({ ...prev, employeeList: value }))}
                />
              </Form.Item>
            </Col>
            <Col>
              <Upload {...uploadProps} showUploadList={false} onChange={handleChangeAttach}>
                <Button
                  icon={<FileExcelOutlined />}
                  type='dashed'
                  style={{
                    minWidth: 120,
                    fontWeight: 500,
                    color: '#1677ff',
                    borderColor: '#91caff',
                    background: '#f0f7ff',
                    transition: 'all 0.2s'
                  }}
                >
                  Upload excel
                </Button>
              </Upload>
            </Col>
          </Row>
        </Form.Item>
        <Form.Item label={t('columnTitle.leavePackage.note')} name='note'>
          <TextArea
            onChange={(e) => setFormState((prev) => ({ ...prev, note: e.target.value }))}
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{ background: '#f8fafc', borderRadius: 6 }}
            placeholder={t('columnTitle.leavePackage.note') as string}
          />
        </Form.Item>
        <Form.Item style={{ marginTop: 24 }}>
          <Button
            onClick={handleSubmit}
            type='primary'
            htmlType='button'
            loading={addLeavePackageMutation.isLoading}
            style={{ width: '100%', fontWeight: 600, fontSize: 16, height: 44 }}
          >
            {t('action.submit')}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}
